import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Play } from 'lucide-react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function RBACAudit() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    try {
      const res = await adminApi.rbacAudit();
      setResults(res.data);
      if (res.data.all_pass) {
        toast.success('All RBAC checks passed!');
      } else {
        toast.error('Some RBAC checks failed');
      }
    } catch (error) {
      toast.error('Failed to run RBAC audit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rbac-audit-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1600px] mx-auto">
            <div className="mb-8">
              <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554] mb-2">RBAC Audit</h1>
              <p className="text-sm text-[#5A6377]">Verify role-based access control and permission enforcement</p>
            </div>

            <div className="bg-white border border-[#E6E9F0] rounded-xl shadow-sm p-6 mb-6">
              <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554] mb-4">Run Security Audit</h2>
              <p className="text-sm text-[#5A6377] mb-4">
                This audit tests the RBAC implementation to ensure proper access control is enforced across all roles.
              </p>
              <Button
                data-testid="run-audit-button"
                onClick={runAudit}
                disabled={loading}
                className="bg-[#2563EB] hover:bg-[#C2151F]"
              >
                <Play className="w-4 h-4 mr-2" />
                {loading ? 'Running Audit...' : 'Run RBAC Audit'}
              </Button>
            </div>

            {results && (
              <div className="bg-white border border-[#E6E9F0] rounded-xl shadow-sm p-6">
                <div className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${results.all_pass ? 'bg-[#E4F6EE]' : 'bg-[#FDE7E9]'}`}>
                  {results.all_pass ? (
                    <>
                      <CheckCircle className="w-6 h-6 text-[#15A36E]" />
                      <div>
                        <p className="text-sm font-semibold text-[#15A36E]">All Checks Passed</p>
                        <p className="text-xs text-[#15A36E]/80">RBAC implementation is secure</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-6 h-6 text-[#E11D2A]" />
                      <div>
                        <p className="text-sm font-semibold text-[#E11D2A]">Some Checks Failed</p>
                        <p className="text-xs text-[#E11D2A]/80">Review failures below</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  {results.checks.map((check, idx) => (
                    <div
                      key={idx}
                      data-testid={`audit-check-${idx}`}
                      className={`flex items-start gap-3 p-4 border rounded-lg ${check.pass ? 'border-[#E4F6EE] bg-[#E4F6EE]/30' : 'border-[#FDE7E9] bg-[#FDE7E9]/30'}`}
                    >
                      {check.pass ? (
                        <CheckCircle className="w-5 h-5 text-[#15A36E] flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-[#E11D2A] flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#11182B] mb-1">{check.check.replace(/_/g, ' ').toUpperCase()}</p>
                        <p className="text-xs text-[#5A6377]">{check.details}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${check.pass ? 'bg-[#15A36E] text-white' : 'bg-[#E11D2A] text-white'}`}>
                        {check.pass ? 'PASS' : 'FAIL'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
